import React, { useEffect, useState } from "react";
import { X, MessageCircle, Send } from "lucide-react";
import { WHATSAPP_NUMBER, WHATSAPP_DIGITS, PROJECT_TYPES, BUDGET_RANGES } from "../data/designOptions";

const initial = { name: "", phone: "", project_type: "", budget_range: "" };

const LeadCaptureModal = ({ open, onClose, onSubmitted, context }) => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setForm(initial);
      setSubmitted(false);
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && open) onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const title =
    context?.trigger === "submit"
      ? "Send Your Design to Design Decor"
      : "Liked this design? Get a free consultation.";

  const subtitle =
    context?.trigger === "submit"
      ? "Share your details and our designer will reach out within 24 hours with a tailored quote."
      : "Our design consultant will reach out with material samples, pricing and a 3D walkthrough — completely free.";

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setError(null);
    if (!form.name.trim() || !form.phone.trim()) {
      setError("Please enter your name and phone number.");
      return;
    }
    setSubmitting(true);
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const res = await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          project_type: form.project_type || PROJECT_TYPES[0],
          budget_range: form.budget_range || BUDGET_RANGES[1],
          source: context?.trigger === "submit" ? "design_submit" : "interaction_popup",
          design_snapshot: context?.design || null,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.detail || "Submission failed");
      }
      setSubmitted(true);
      onSubmitted?.();
    } catch (err) {
      setError(err.message || "Something went wrong. Please try WhatsApp.");
    } finally {
      setSubmitting(false);
    }
  };

  const buildWhatsAppMsg = () => {
    const parts = [
      `Hi Design Decor, I'd like a free consultation.`,
      form.name && `Name: ${form.name}`,
      form.phone && `Phone: ${form.phone}`,
      form.project_type && `Project: ${form.project_type}`,
      form.budget_range && `Budget: ${form.budget_range}`,
      context?.design && `Design: ${context.design.design_type} configurator`,
    ].filter(Boolean);
    return encodeURIComponent(parts.join("\n"));
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[100] flex items-end justify-center p-4 sm:items-center"
      data-testid="lead-capture-modal"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/75 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      {/* Card */}
      <div className="relative w-full max-w-xl border border-[#B8996F]/30 bg-[#0F0D0B] text-[#F7F5F0] shadow-[0_30px_120px_-20px_rgba(184,153,111,0.35)]">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          data-testid="lead-modal-close"
          className="absolute right-3 top-3 p-2 text-white/60 transition-colors duration-300 hover:text-white"
        >
          <X size={18} />
        </button>

        <div className="px-6 py-7 sm:px-8 sm:py-9">
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#B8996F]">
            Design Decor · Free Consultation
          </span>
          <h3 className="mt-3 font-[Cormorant_Garamond] text-3xl font-light leading-tight">
            {title}
          </h3>
          <p className="mt-2 font-[Outfit] text-sm text-white/65">{subtitle}</p>

          {submitted ? (
            <div className="mt-6 space-y-4" data-testid="lead-success">
              <div className="border border-[#B8996F]/40 bg-[#B8996F]/10 px-4 py-4">
                <div className="font-[Cormorant_Garamond] text-xl">Thank you, {form.name || "friend"}.</div>
                <div className="mt-1 text-sm text-white/70">
                  Our design consultant will contact you on {form.phone} shortly. To get an instant
                  reply, connect with us on WhatsApp.
                </div>
              </div>
              <a
                href={`https://wa.me/${WHATSAPP_DIGITS}?text=${buildWhatsAppMsg()}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="lead-whatsapp-success"
                className="flex w-full items-center justify-center gap-2 bg-[#25D366] px-5 py-3 text-[12px] uppercase tracking-[0.22em] text-[#0A0A0A] transition-colors duration-300 hover:bg-[#22c55e]"
              >
                <MessageCircle size={16} strokeWidth={1.8} /> Continue on WhatsApp ({WHATSAPP_NUMBER})
              </a>
              <button
                type="button"
                onClick={onClose}
                className="w-full border border-white/15 px-5 py-3 text-[11px] uppercase tracking-[0.22em] text-white/75 transition-colors duration-300 hover:border-white/40 hover:text-white"
              >
                Close
              </button>
            </div>
          ) : (
            <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.22em] text-white/45">Name</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    data-testid="lead-input-name"
                    className="mt-1 w-full border border-white/15 bg-transparent px-3 py-2.5 font-[Outfit] text-sm text-white outline-none transition-colors duration-300 focus:border-[#B8996F]"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.22em] text-white/45">Phone</label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    data-testid="lead-input-phone"
                    className="mt-1 w-full border border-white/15 bg-transparent px-3 py-2.5 font-[Outfit] text-sm text-white outline-none transition-colors duration-300 focus:border-[#B8996F]"
                    placeholder="+91 9XXXXXXXXX"
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.22em] text-white/45">Project Type</label>
                  <select
                    value={form.project_type}
                    onChange={(e) => update("project_type", e.target.value)}
                    data-testid="lead-input-project-type"
                    className="mt-1 w-full border border-white/15 bg-[#0F0D0B] px-3 py-2.5 font-[Outfit] text-sm text-white outline-none transition-colors duration-300 focus:border-[#B8996F]"
                  >
                    <option value="" className="bg-[#0F0D0B] text-white">Select…</option>
                    {PROJECT_TYPES.map((p) => (
                      <option key={p} value={p} className="bg-[#0F0D0B] text-white">{p}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.22em] text-white/45">Budget Range</label>
                  <select
                    value={form.budget_range}
                    onChange={(e) => update("budget_range", e.target.value)}
                    data-testid="lead-input-budget"
                    className="mt-1 w-full border border-white/15 bg-[#0F0D0B] px-3 py-2.5 font-[Outfit] text-sm text-white outline-none transition-colors duration-300 focus:border-[#B8996F]"
                  >
                    <option value="" className="bg-[#0F0D0B] text-white">Select…</option>
                    {BUDGET_RANGES.map((b) => (
                      <option key={b} value={b} className="bg-[#0F0D0B] text-white">{b}</option>
                    ))}
                  </select>
                </div>
              </div>

              {error && (
                <div
                  className="border border-red-400/40 bg-red-500/10 px-3 py-2 text-xs text-red-200"
                  data-testid="lead-error"
                >
                  {error}
                </div>
              )}

              <div className="flex flex-col gap-2 pt-2 sm:flex-row">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="lead-submit-btn"
                  className="flex flex-1 items-center justify-center gap-2 bg-[#B8996F] px-5 py-3 text-[12px] uppercase tracking-[0.22em] text-[#0A0A0A] transition-colors duration-300 hover:bg-[#F7F5F0] disabled:opacity-60"
                >
                  <Send size={14} strokeWidth={1.8} />
                  {submitting ? "Submitting…" : "Request Consultation"}
                </button>
                <a
                  href={`https://wa.me/${WHATSAPP_DIGITS}?text=${buildWhatsAppMsg()}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="lead-whatsapp-btn"
                  className="flex flex-1 items-center justify-center gap-2 border border-[#25D366] bg-transparent px-5 py-3 text-[12px] uppercase tracking-[0.22em] text-[#25D366] transition-colors duration-300 hover:bg-[#25D366] hover:text-[#0A0A0A]"
                >
                  <MessageCircle size={14} strokeWidth={1.8} />
                  WhatsApp Quick Connect
                </a>
              </div>

              <p className="pt-2 text-[10px] uppercase tracking-[0.22em] text-white/35">
                We respect your privacy. No spam — ever.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadCaptureModal;
